import { useLingui } from '@lingui/react/macro'
import { PaginationState, Row, SortingState } from '@tanstack/react-table'
import { CollapsibleSectionHeader } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/common/CollapsibleSectionHeader'
import { SearchTableWrapper } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/SearchTableWrapper'
import { GeckoPowerTable } from 'features/knowledgeDocumentViewer/components/table/GeckoPowerTable'
import { SensorSearchItem } from 'features/knowledgeDocumentViewer/types/searchableItems'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { SensorTableColumns } from './SensorTableColumns'

const Container = styled.div<{ $fullPage?: boolean }>`
  flex-shrink: ${(props) => (props.$fullPage ? '1' : '0')};
  flex: ${(props) => (props.$fullPage ? '1' : 'unset')};
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-bottom: ${(props) =>
    props.$fullPage ? 'none' : `1px solid ${props.theme.forecast.border}`};
`

const InlineTableWrapper = styled(SearchTableWrapper)`
  flex: unset;

  .rt-ScrollAreaRoot {
    max-height: 12rem;
    overflow-y: auto;
  }
`

const DEFAULT_PAGINATION: PaginationState = { pageIndex: 0, pageSize: 50 }

interface SensorResultsSectionProps {
  sensors: SensorSearchItem[]
  totalCount: number
  isLoading: boolean
  onSensorClick: (sensor: SensorSearchItem) => void
  onSensorDoubleClick?: (sensor: SensorSearchItem) => void
  selectedSensorId?: string | null
  fullPage?: boolean
}

export const SensorResultsSection: React.FC<SensorResultsSectionProps> = ({
  sensors,
  totalCount,
  isLoading,
  onSensorClick,
  onSensorDoubleClick,
  selectedSensorId,
  fullPage = false,
}) => {
  const { t } = useLingui()
  const [isExpanded, setIsExpanded] = useState(false)
  const [sorting, setSorting] = useState<SortingState>([])
  const [pagination, setPagination] =
    useState<PaginationState>(DEFAULT_PAGINATION)

  const columns = useMemo(() => SensorTableColumns(t), [t])

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  const handleRowSelect = useCallback(
    (row: Row<SensorSearchItem>) => {
      onSensorClick(row.original)
    },
    [onSensorClick]
  )

  const handleRowClick = useCallback(
    (
      row: Row<SensorSearchItem>,
      event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
    ) => {
      onSensorClick(row.original)
      if (event.detail === 2) {
        onSensorDoubleClick?.(row.original)
      }
    },
    [onSensorClick, onSensorDoubleClick]
  )

  if (!fullPage && !isLoading && sensors.length === 0) {
    return null
  }

  const tableComponentProps = {
    columns: columns,
    data: sensors,
    isLoadingData: isLoading,
    footerName: t`Sensors`,
    pagination: pagination,
    setPagination: setPagination,
    sorting: sorting,
    setSorting: setSorting,
    showTable: true,
    onRowClick: handleRowClick,
    onRowFocus: handleRowSelect,
    showFilterBar: true,
    disableUrlFilters: true,
    showFooter: true,
    isTableHeaderSticky: true,
    showColumnConfig: false,
    showHorizontalScrollbar: true,
    highlightSelectedRow: true,
    getRowId: (row: SensorSearchItem) => row.id,
    controlledHighlightedRowId: selectedSensorId ?? null,
  }

  if (fullPage) {
    return (
      <Container $fullPage>
        <SearchTableWrapper>
          <GeckoPowerTable {...tableComponentProps} />
        </SearchTableWrapper>
      </Container>
    )
  }

  return (
    <Container>
      <CollapsibleSectionHeader
        label={t`Sensors`}
        count={isLoading ? '...' : totalCount}
        isExpanded={isExpanded}
        onToggle={handleToggle}
      />
      {isExpanded && (
        <InlineTableWrapper>
          <GeckoPowerTable {...tableComponentProps} />
        </InlineTableWrapper>
      )}
    </Container>
  )
}
